import type { QariProfile } from '@/types/qari';
import { useQariDocuments } from '@/hooks/useQariProfile';
import { QARI_DOCUMENT_KIND_LABELS } from '@/types/qari';

type Props = {
  profile: QariProfile;
};

function ReviewRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-3 gap-4 py-2.5 border-b border-ink-200/50 dark:border-ink-800/50 last:border-b-0">
      <dt className="text-xs uppercase tracking-[0.18em] text-ink-500 dark:text-ink-400 col-span-1">
        {label}
      </dt>
      <dd className="text-sm text-ink-900 dark:text-ink-50 col-span-2 leading-relaxed">
        {value || (
          <span className="text-ink-400 dark:text-ink-600 italic">
            Not provided
          </span>
        )}
      </dd>
    </div>
  );
}

export function StepReview({ profile }: Props) {
  const docs = useQariDocuments();

  return (
    <div className="space-y-8">
      <p className="text-sm text-ink-600 dark:text-ink-300 leading-relaxed max-w-prose">
        Review what you've entered. Once submitted, your profile moves to
        Pending review. You can withdraw or revise while it's pending.
      </p>

      <section>
        <h3 className="font-serif text-lg text-ink-900 dark:text-ink-50 mb-2">
          Personal
        </h3>
        <dl>
          <ReviewRow label="Full name" value={profile.full_name} />
          <ReviewRow label="Bio" value={profile.bio} />
          <ReviewRow
            label="Location"
            value={[profile.city, profile.country].filter(Boolean).join(', ')}
          />
          <ReviewRow
            label="Languages"
            value={profile.languages.join(', ')}
          />
        </dl>
      </section>

      <section>
        <h3 className="font-serif text-lg text-ink-900 dark:text-ink-50 mb-2">
          Qualifications
        </h3>
        <dl>
          <ReviewRow
            label="Years"
            value={
              profile.years_experience !== null
                ? `${profile.years_experience} year(s)`
                : null
            }
          />
          <ReviewRow label="Qira'at" value={profile.qiraat.join(', ')} />
          <ReviewRow
            label="Specializations"
            value={profile.specializations.join(', ')}
          />
          <ReviewRow label="Hifz" value={profile.hifz_status} />
          <ReviewRow label="Email" value={profile.contact_email} />
          <ReviewRow label="Phone" value={profile.contact_phone} />
        </dl>
      </section>

      <section>
        <h3 className="font-serif text-lg text-ink-900 dark:text-ink-50 mb-2">
          Documents
        </h3>
        {docs.isLoading && (
          <p className="text-sm text-ink-500 dark:text-ink-400">Loading…</p>
        )}
        {docs.data && docs.data.length === 0 && (
          <p className="text-sm text-ink-500 dark:text-ink-400 italic">
            No documents uploaded.
          </p>
        )}
        {docs.data && docs.data.length > 0 && (
          <ul className="text-sm text-ink-700 dark:text-ink-200 space-y-1">
            {docs.data.map((d) => (
              <li key={d.id}>
                <span className="text-ink-500 dark:text-ink-400">
                  {QARI_DOCUMENT_KIND_LABELS[d.kind]}:
                </span>{' '}
                {d.original_filename}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
